import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Mail, KeyRound, Users, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * PasswordChangesPage — R112 audit feed.
 *
 * Every admin-initiated password action (single reset link, direct-set,
 * bulk reset, bulk direct-set) appears here for compliance review. Rows
 * link to the target user by email so a reviewer can eyeball unusual
 * patterns (same admin resetting the same user twice in an hour, an
 * out-of-hours direct-set on an admin account, etc.).
 */

function methodBadge(method) {
  const map = {
    email_link:       { label: "Email link",       tone: "border-orange-500/30 text-orange-300" },
    direct_set:       { label: "Direct set",       tone: "border-indigo-500/30 text-indigo-300" },
    bulk_email_link:  { label: "Bulk · email",     tone: "border-orange-500/40 text-orange-200" },
    bulk_direct_set:  { label: "Bulk · direct",    tone: "border-indigo-500/40 text-indigo-200" },
  };
  const cfg = map[method] || { label: method, tone: "border-white/10 text-zinc-400" };
  return <Badge variant="outline" className={cfg.tone + " text-[10px] uppercase tracking-widest"}>{cfg.label}</Badge>;
}

export default function PasswordChangesPage() {
  const [state, setState] = useState({ items: [], count: 0, action_counts: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/system/password-changes?limit=200");
      setState(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load password changes");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = state.action_counts || {};
  const totalIndividual = (counts["user.reset_password"] || 0) + (counts["user.admin_set_password"] || 0);
  const totalBulk = (counts["user.bulk_reset_password"] || 0) + (counts["user.bulk_set_password"] || 0);
  const totalFailed = state.items.filter((i) => i.delivered === false).length;

  return (
    <div className="p-8 space-y-6" data-testid="password-changes-page">
      <PageHeader
        title="Password change log"
        subtitle="Every admin-initiated password action, in one compliance-ready feed."
        actions={
          <Button variant="outline" onClick={load} className="border-white/10 gap-1 h-9" data-testid="pw-log-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <KeyRound className="h-3.5 w-3.5" /> Total events
          </div>
          <div className="text-3xl font-semibold mt-2 text-emerald-300" data-testid="pw-log-total">{state.count}</div>
        </Card>
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <Users className="h-3.5 w-3.5" /> Individual
          </div>
          <div className="text-3xl font-semibold mt-2 text-orange-300" data-testid="pw-log-individual">{totalIndividual}</div>
          <div className="text-[10px] text-zinc-500 mt-1">
            {counts["user.reset_password"] || 0} link · {counts["user.admin_set_password"] || 0} direct
          </div>
        </Card>
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <Users className="h-3.5 w-3.5" /> Bulk
          </div>
          <div className="text-3xl font-semibold mt-2 text-indigo-300" data-testid="pw-log-bulk">{totalBulk}</div>
          <div className="text-[10px] text-zinc-500 mt-1">
            {counts["user.bulk_reset_password"] || 0} link · {counts["user.bulk_set_password"] || 0} direct
          </div>
        </Card>
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <AlertTriangle className="h-3.5 w-3.5" /> Delivery failed
          </div>
          <div className={"text-3xl font-semibold mt-2 " + (totalFailed ? "text-rose-300" : "text-emerald-300")}
                data-testid="pw-log-failed">{totalFailed}</div>
        </Card>
      </div>

      <Card className="border-white/10 bg-white/[0.02] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Loading…</div>
        ) : state.items.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            No password change events recorded yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="pw-log-list">
            <thead className="bg-white/[0.03] text-xs">
              <tr>
                <th className="text-left px-3 py-2 text-zinc-400">When</th>
                <th className="text-left px-3 py-2 text-zinc-400">Actor</th>
                <th className="text-left px-3 py-2 text-zinc-400">Target</th>
                <th className="text-left px-3 py-2 text-zinc-400">Method</th>
                <th className="text-left px-3 py-2 text-zinc-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {state.items.map((r) => (
                <tr key={r.id} className="border-t border-white/5" data-testid={`pw-row-${r.id}`}>
                  <td className="px-3 py-2 text-xs text-zinc-400">{new Date(r.at).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <div className="text-zinc-100 font-medium">{r.actor_name || r.actor_id}</div>
                    <div className="text-[10px] text-zinc-500">{r.actor_role}</div>
                  </td>
                  <td className="px-3 py-2">
                    {r.target_id === "bulk" ? (
                      <div className="text-zinc-300 text-xs italic">
                        {r.attempted != null ? `${r.applied ?? r.delivered_count ?? 0} of ${r.attempted} users` : "bulk"}
                      </div>
                    ) : (
                      <>
                        <div className="text-zinc-100">{r.target_email || r.target_name || r.target_id}</div>
                        {r.target_name && r.target_email && (
                          <div className="text-[10px] text-zinc-500">{r.target_name}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {methodBadge(r.method)}
                    {r.force_change_on_login && (
                      <Badge variant="outline" className="ml-1 border-amber-500/30 text-amber-300 text-[9px]">
                        force change
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.method.includes("direct") ? (
                      <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-[10px]">applied</Badge>
                    ) : r.delivered === true ? (
                      <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-[10px]">
                        <Mail className="h-3 w-3 mr-1" /> delivered
                      </Badge>
                    ) : r.delivered === false ? (
                      <div>
                        <Badge variant="outline" className="border-rose-500/30 text-rose-300 text-[10px]">
                          <AlertTriangle className="h-3 w-3 mr-1" /> failed
                        </Badge>
                        {r.error && (
                          <div className="text-[10px] text-rose-400/80 mt-1 truncate max-w-xs" title={r.error}>
                            {r.error.slice(0, 60)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Badge variant="outline" className="border-zinc-500/30 text-zinc-400 text-[10px]">n/a</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
